import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosClient, getBaseUrlVersioned } from "../../services/axiosClient";
import { API_VERSION } from "../types";
import buildGetLorebookByIdUrl from "./buildGetLorebookByIdUrl";
import { LorebookFlow } from "./types";
const API_PATH_LOREBOOK = "lorebooks";
export const getLorebookById = createAsyncThunk("lorebook/getById", async ({ id, lastEntryId, lastSortPriority, search, entryStatus, view, }, thunkAPI) => {
    try {
        const baseUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_LOREBOOK}/${id}`;
        const apiUrl = buildGetLorebookByIdUrl({
            baseUrl,
            lastEntryId,
            lastSortPriority,
            search,
            entryStatus,
            view,
        });
        const response = await axiosClient.get(apiUrl);
        return response.data;
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const createLorebook = createAsyncThunk("lorebook/create", async (params, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_LOREBOOK}`;
        const response = await axiosClient.post(apiUrl, params);
        return response.data;
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const importLorebook = createAsyncThunk("lorebook/import", async (params, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_LOREBOOK}/import`;
        const response = await axiosClient.post(apiUrl, params, {
            maxBodyLength: Infinity,
        });
        return response.data;
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const suggestLorebookField = createAsyncThunk("lorebook/suggestField", async (params, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_LOREBOOK}/suggest_field`;
        const response = await axiosClient.post(apiUrl, params, {
            maxBodyLength: Infinity,
        });
        return response.data;
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const createLorebookEntry = createAsyncThunk("lorebook/createEntry", async ({ id, params }, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_LOREBOOK}/${id}/entries`;
        const response = await axiosClient.post(apiUrl, params);
        return response.data;
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const updateLorebook = createAsyncThunk("lorebook/update", async ({ id, params }, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_LOREBOOK}/${id}`;
        const response = await axiosClient.patch(apiUrl, params);
        return response.data;
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
/**
 * Submit staged public-lorebook edits (parent fields + aggregated entry_ops) for moderation.
 *
 * Hits the same `PATCH /lorebooks/{id}` endpoint as {@link updateLorebook}; the backend decides
 * server-side (from visibility + moderation flags) whether to stage the edit as a pending
 * moderation snapshot or write it live. Kept as a distinct thunk from `updateLorebook` so the
 * moderation payload type (`PatchLorebookModerationRequest`, carrying `entry_ops`) stays explicit
 * and the normal details-save path is unaffected.
 */
export const submitLorebookModeration = createAsyncThunk("lorebook/submitModeration", async ({ id, params }, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_LOREBOOK}/${id}`;
        const response = await axiosClient.patch(apiUrl, params);
        return response.data;
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const updateLorebookEntry = createAsyncThunk("lorebook/updateEntry", async ({ id, entryId, params, }, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_LOREBOOK}/${id}/entries/${entryId}`;
        const response = await axiosClient.patch(apiUrl, params);
        return response.data;
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const deleteLorebook = createAsyncThunk("lorebook/delete", async ({ id }, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_LOREBOOK}/${id}`;
        await axiosClient.delete(apiUrl);
        return null;
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const deleteLorebookEntry = createAsyncThunk("lorebook/delete", async ({ id, entryId }, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_LOREBOOK}/${id}/entries/${entryId}`;
        await axiosClient.delete(apiUrl);
        return null;
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const attachLorebook = createAsyncThunk("lorebook/attach", async ({ id, params }, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_LOREBOOK}/${id}/attach`;
        await axiosClient.post(apiUrl, params);
        return null;
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const detachLorebook = createAsyncThunk("lorebook/detach", async ({ id, characterId }, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_LOREBOOK}/${id}/detach/${characterId}`;
        await axiosClient.delete(apiUrl);
        return null;
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const getLorebooks = createAsyncThunk("lorebook/getAll", async (params, thunkAPI) => {
    try {
        let apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_LOREBOOK}`;
        if (params?.triggerError) {
            apiUrl += "?triggerError=true";
        }
        const response = await axiosClient.get(apiUrl);
        return response.data;
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const reportLorebook = createAsyncThunk("lorebook/report", async ({ id, params }, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_LOREBOOK}/${id}/report`;
        const response = await axiosClient.post(apiUrl, params);
        return response.data;
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const cloneLorebook = createAsyncThunk("lorebook/clone", async ({ id }, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_LOREBOOK}/${id}/clone`;
        const response = await axiosClient.post(apiUrl, {});
        return response.data;
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
const initialState = {
    loading: false,
    loadingType: null,
    lorebookData: null,
    lorebooksData: null,
};
const handlePending = (state, loadingType) => {
    state.loading = true;
    state.loadingType = loadingType;
};
const handleFulfilled = (state) => {
    state.loading = false;
    state.loadingType = null;
};
const handleRejected = (state, loadingType) => {
    state.loading = false;
    state.loadingType = loadingType;
};
export const lorebookSlice = createSlice({
    name: "lorebook",
    initialState: initialState,
    reducers: {
        clearLorebookLoading: (state) => {
            state.loading = false;
            state.loadingType = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getLorebookById.pending, (state) => {
            handlePending(state, LorebookFlow.GET_LOREBOOK);
        });
        builder.addCase(getLorebookById.fulfilled, (state, action) => {
            handleFulfilled(state);
            state.lorebookData = action.payload;
        });
        builder.addCase(getLorebookById.rejected, (state) => {
            handleRejected(state, LorebookFlow.GET_LOREBOOK);
        });
        builder.addCase(getLorebooks.pending, (state) => {
            handlePending(state, LorebookFlow.GET_LOREBOOKS);
        });
        builder.addCase(getLorebooks.fulfilled, (state, action) => {
            handleFulfilled(state);
            state.lorebooksData = action.payload;
        });
        builder.addCase(getLorebooks.rejected, (state) => {
            handleRejected(state, LorebookFlow.GET_LOREBOOKS);
        });
        builder.addCase(createLorebook.pending, (state) => {
            handlePending(state, LorebookFlow.CREATE_LOREBOOK);
        });
        builder.addCase(createLorebook.fulfilled, (state, action) => {
            handleFulfilled(state);
            state.lorebookData = action.payload;
        });
        builder.addCase(createLorebook.rejected, (state) => {
            handleRejected(state, LorebookFlow.CREATE_LOREBOOK);
        });
        builder.addCase(importLorebook.pending, (state) => {
            handlePending(state, LorebookFlow.IMPORT_LOREBOOK);
        });
        builder.addCase(importLorebook.fulfilled, (state, action) => {
            handleFulfilled(state);
            state.lorebookData = action.payload;
        });
        builder.addCase(importLorebook.rejected, (state) => {
            handleRejected(state, LorebookFlow.IMPORT_LOREBOOK);
        });
        builder.addCase(updateLorebook.pending, (state) => {
            handlePending(state, LorebookFlow.UPDATE_LOREBOOK);
        });
        builder.addCase(updateLorebook.fulfilled, (state, action) => {
            handleFulfilled(state);
            state.lorebookData = action.payload;
        });
        builder.addCase(updateLorebook.rejected, (state) => {
            handleRejected(state, LorebookFlow.UPDATE_LOREBOOK);
        });
        builder.addCase(submitLorebookModeration.pending, (state) => {
            handlePending(state, LorebookFlow.SUBMIT_LOREBOOK_MODERATION);
        });
        builder.addCase(submitLorebookModeration.fulfilled, (state, action) => {
            handleFulfilled(state);
            state.lorebookData = action.payload;
        });
        builder.addCase(submitLorebookModeration.rejected, (state) => {
            handleRejected(state, LorebookFlow.SUBMIT_LOREBOOK_MODERATION);
        });
        builder.addCase(deleteLorebook.pending, (state) => {
            handlePending(state, LorebookFlow.DELETE_LOREBOOK);
        });
        builder.addCase(deleteLorebook.fulfilled, (state) => {
            handleFulfilled(state);
            // Clear the lorebook data after successful deletion
            state.lorebookData = null;
        });
        builder.addCase(deleteLorebook.rejected, (state) => {
            handleRejected(state, LorebookFlow.DELETE_LOREBOOK);
        });
        builder.addCase(suggestLorebookField.pending, (state) => {
            handlePending(state, LorebookFlow.SUGGEST_FIELD);
        });
        builder.addCase(suggestLorebookField.fulfilled, (state) => {
            handleFulfilled(state);
        });
        builder.addCase(suggestLorebookField.rejected, (state) => {
            handleRejected(state, LorebookFlow.SUGGEST_FIELD);
        });
        builder.addCase(createLorebookEntry.pending, (state) => {
            handlePending(state, LorebookFlow.CREATE_LOREBOOK_ENTRY);
        });
        builder.addCase(createLorebookEntry.fulfilled, (state) => {
            handleFulfilled(state);
        });
        builder.addCase(createLorebookEntry.rejected, (state) => {
            handleRejected(state, LorebookFlow.CREATE_LOREBOOK_ENTRY);
        });
        builder.addCase(updateLorebookEntry.pending, (state) => {
            handlePending(state, LorebookFlow.UPDATE_LOREBOOK_ENTRY);
        });
        builder.addCase(updateLorebookEntry.fulfilled, (state) => {
            handleFulfilled(state);
        });
        builder.addCase(updateLorebookEntry.rejected, (state) => {
            handleRejected(state, LorebookFlow.UPDATE_LOREBOOK_ENTRY);
        });
        builder.addCase(reportLorebook.pending, (state) => {
            handlePending(state, LorebookFlow.REPORT_LOREBOOK);
        });
        builder.addCase(reportLorebook.fulfilled, (state) => {
            handleFulfilled(state);
        });
        builder.addCase(reportLorebook.rejected, (state) => {
            handleRejected(state, LorebookFlow.REPORT_LOREBOOK);
        });
        builder.addCase(cloneLorebook.pending, (state) => {
            handlePending(state, LorebookFlow.CLONE_LOREBOOK);
        });
        builder.addCase(cloneLorebook.fulfilled, (state, action) => {
            handleFulfilled(state);
            state.lorebookData = action.payload;
        });
        builder.addCase(cloneLorebook.rejected, (state) => {
            handleRejected(state, LorebookFlow.CLONE_LOREBOOK);
        });
    },
});
export const { clearLorebookLoading } = lorebookSlice.actions;
export default lorebookSlice.reducer;
